import React, { useState, useMemo } from 'react';
import { BookOpen, Search, ChevronRight, HelpCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getHelpArticle } from '@/services/helpService';

// Import help documents (we'll need to expose this from the backend or create a shared type)
// For now, let's define the structure and fetch from an API endpoint or use a static list
interface HelpDocument {
  id: string;
  title: string;
  category: string;
  keywords: string[];
  content: string;
  relatedFeatures?: string[];
}

// Static list of help document IDs and titles organized by category
// In production, this would come from an API endpoint
const HELP_DOCUMENTS_BY_CATEGORY: Record<string, Array<{ id: string; title: string }>> = {
  'Compliances/Policies': [
    { id: 'policy_create', title: 'Creating a New Compliance Policy' },
    { id: 'policy_manage', title: 'Managing Policies' },
  ],
  'Locations': [
    { id: 'location_create', title: 'Adding Locations' },
    { id: 'location_manage', title: 'Managing Locations' },
  ],
  'Tasks': [
    { id: 'task_create', title: 'Creating Tasks' },
    { id: 'task_complete', title: 'Completing Tasks' },
    { id: 'task_manage', title: 'Managing Tasks' },
  ],
  'Contracts': [
    { id: 'contract_template', title: 'Contract Templates' },
    { id: 'contract_upload', title: 'Uploading Contracts' },
    { id: 'contract_vendor_upload', title: 'Uploading Vendor Contracts' },
    { id: 'contract_employee_upload', title: 'Uploading Employee Contracts' },
    { id: 'contract_manage', title: 'Managing Contracts' },
    { id: 'contract_renewal', title: 'Contract Renewal Process' },
  ],
  'Checklists': [
    { id: 'checklist_create', title: 'Creating Checklists' },
    { id: 'checklist_run', title: 'Running/Completing Checklists' },
    { id: 'checklist_manage', title: 'Managing Checklists' },
  ],
  'Forms': [
    { id: 'form_create', title: 'Creating Forms' },
    { id: 'form_fill', title: 'Filling Out Forms' },
    { id: 'form_manage', title: 'Managing Forms' },
  ],
  'Assets': [
    { id: 'asset_create', title: 'Adding Assets' },
    { id: 'asset_manage', title: 'Managing Assets' },
    { id: 'qr_code', title: 'QR Codes for Assets' },
  ],
  'Users': [
    { id: 'user_add', title: 'Adding Users' },
    { id: 'user_manage', title: 'Managing Users' },
  ],
  'General': [
    { id: 'navigation', title: 'Application Navigation' },
    { id: 'dashboard', title: 'Understanding the Dashboard' },
    { id: 'evidence_vault', title: 'Using the Evidence Vault' },
    { id: 'settings', title: 'Application Settings' },
  ],
};

const HelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const [articleContent, setArticleContent] = useState<string | null>(null);

  const categories = Object.keys(HELP_DOCUMENTS_BY_CATEGORY);

  // Filter documents based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return HELP_DOCUMENTS_BY_CATEGORY;
    }

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, Array<{ id: string; title: string }>> = {};

    Object.entries(HELP_DOCUMENTS_BY_CATEGORY).forEach(([category, docs]) => {
      const matchingDocs = docs.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.id.toLowerCase().includes(query)
      );
      if (matchingDocs.length > 0) {
        filtered[category] = matchingDocs;
      }
    });

    return filtered;
  }, [searchQuery]);

  const handleArticleClick = React.useCallback(async (articleId: string) => {
    setSelectedArticle(articleId);
    setArticleContent(null); // Clear previous content while loading
    
    try {
      const article = await getHelpArticle(articleId);
      setArticleContent(article.content);
    } catch (error: any) {
      console.error('[HelpCenter] Error fetching article:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      
      // Get the article title for better error message
      const articleTitle = Object.values(HELP_DOCUMENTS_BY_CATEGORY)
        .flat()
        .find(doc => doc.id === articleId)?.title || articleId;
      
      let userMessage = `**Unable to load article from server.**\n\n`;
      
      if (errorMessage.includes('Cannot connect') || errorMessage.includes('Failed to fetch') || errorMessage.includes('network')) {
        userMessage += `The help service may not be deployed yet or there's a network issue. You can:\n\n`;
        userMessage += `1. Use the AI chat to ask questions about "${articleTitle}"\n`;
        userMessage += `2. Check the browser console (F12) for more details\n`;
        userMessage += `3. Contact support if the issue persists\n\n`;
        userMessage += `Error: ${errorMessage}`;
      } else {
        userMessage += `${errorMessage}\n\n`;
        userMessage += `You can use the AI chat to ask questions about "${articleTitle}".`;
      }
      
      setArticleContent(userMessage);
    }
  }, []);

  // Handle URL parameters for direct article/category links
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const articleParam = params.get('article');
    const categoryParam = params.get('category');
    
    if (articleParam) {
      handleArticleClick(articleParam);
    } else if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [handleArticleClick]);

  const selectedDoc = selectedArticle 
    ? Object.values(HELP_DOCUMENTS_BY_CATEGORY)
        .flat()
        .find(doc => doc.id === selectedArticle)
    : null;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="w-8 h-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Help Center</h1>
        </div>
        <p className="text-gray-600">
          Find answers to common questions and learn how to use Complin.ai features.
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Categories and Articles */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4">
            <h2 className="font-semibold text-gray-900 mb-4">Categories</h2>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedArticle(null);
                  setArticleContent(null);
                }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Articles
              </button>
              {categories.map((category) => (
                <div key={category}>
                  <button
                    onClick={() => {
                      setSelectedCategory(category);
                      setSelectedArticle(null);
                      setArticleContent(null);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                      selectedCategory === category
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{category}</span>
                    <span className="text-xs text-gray-500">
                      {HELP_DOCUMENTS_BY_CATEGORY[category].length}
                    </span>
                  </button>
                  {selectedCategory === category && (
                    <div className="ml-4 mt-1 space-y-1">
                      {filteredCategories[category]?.map((doc) => (
                        <button
                          key={doc.id}
                          onClick={() => handleArticleClick(doc.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                            selectedArticle === doc.id
                              ? 'bg-primary-100 text-primary-800 font-medium'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {doc.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          {selectedArticle && articleContent === null ? (
            // Article selected but content loading
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedDoc?.title}</h2>
                <button
                  onClick={() => {
                    setSelectedArticle(null);
                    setArticleContent(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="prose max-w-none">
                <p className="text-gray-600">Loading article...</p>
              </div>
            </div>
          ) : selectedArticle ? (
            // Article with content
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedDoc?.title}</h2>
                <button
                  onClick={() => {
                    setSelectedArticle(null);
                    setArticleContent(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="prose max-w-none">
                {/* Render markdown content - in production you'd use a markdown renderer like react-markdown */}
                <div className="whitespace-pre-wrap text-gray-700">{articleContent}</div>
              </div>
            </div>
          ) : (
            // Article List View
            <div className="space-y-6">
              {(selectedCategory
                ? { [selectedCategory]: filteredCategories[selectedCategory] }
                : filteredCategories
              ) && Object.entries(selectedCategory
                ? { [selectedCategory]: filteredCategories[selectedCategory] }
                : filteredCategories
              ).map(([category, docs]) => (
                <div key={category} className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">{category}</h2>
                  <div className="space-y-3">
                    {docs.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => handleArticleClick(doc.id)}
                        className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-start gap-3">
                            <HelpCircle className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-gray-900 group-hover:text-primary-700">
                                {doc.title}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                Click to read full article
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {Object.keys(filteredCategories).length === 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
                  <p className="text-gray-600">
                    Try adjusting your search query or{' '}
                    <Link to="/home" className="text-primary-600 hover:underline">
                      ask the AI assistant
                    </Link>
                    .
                  </p>
                </div>
              )}

              {/* Quick Access to AI Chat */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <HelpCircle className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">Need more help?</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Can't find what you're looking for? Our AI assistant can answer specific questions and guide you through any feature.
                    </p>
                    <button
                      onClick={() => {
                        // Open AI chat widget (would integrate with your chat system)
                        window.dispatchEvent(new CustomEvent('open-ai-chat'));
                      }}
                      className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-2"
                    >
                      Ask AI Assistant
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;

